// frontend/src/components/Pages/DashboardGeneral.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Form, InputGroup, Row, Col } from "react-bootstrap"; // Modal, Button removed
import axios from "axios";
import authService from '../../services/authService'; // Ensure this path is correct
import { useNavigate, Link } from 'react-router-dom';
import './DashboardGeneral.css'; // Or import './DashboardGeodesy.css' if you've aligned the CSS

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
  </svg>
);

// Options for filters and summary - taken from DashboardGeodesy.js (adapt names if needed)
const statusOptionsForSummaryAndFilter = [ // Renamed from statusOptionsForGeodesy for clarity
  "1st_proof", "2nd_proof", "3rd_proof", "Geodesy", "Boundary_section",
  "GIS", "Digital_Carto", "Director_office", "DPCO", "CTP", "LPO",
  // "Pending", "In Progress", "Completed", "Submitted" // These are usually for working_status
];
const partyUnitOptions = [
  "1Party", "2Party", "3Party", "4Party", "5Party",
  "1Carto", "2Carto", "Photo_office"
];
const seasonOptions = [
  "2022-2023", "2023-2024", "2024-2025", "2025-2026",
  "2026-2027", "2027-2028", "2028-2029", "2029-2030",
  "2030-2031", "2031-2032"
];
const workingStatusOptions = [ // For row highlighting
  "In progress", "Pending", "Submitted", "Completed"
];


function DashboardGeneral() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartyUnitFilter, setSelectedPartyUnitFilter] = useState('');
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState('');

  const navigate = useNavigate();

  // IMPORTANT: This columnStructure is taken from DashboardGeodesy.js.
  // Modify it if DashboardGeneral needs to display different columns.
  const columnStructure = useMemo(() => [
    { label: "SL", category: "General", fields: [{ name: "sl", label: "SL" }] },
    { label: "Upazila sheet/Sheet number", category: "General", fields: [{ name: "upazila_sheet", label: "Upazila sheet/Sheet number" }] },
    { label: "Party/Unit", category: "General", fields: [{ name: "party_unit", label: "Party/Unit" }] },
    { label: "Surveyor name", category: "General", fields: [{ name: "surveyor_name", label: "Surveyor name" }] },
    { label: "Status", category: "General", fields: [{ name: "status", label: "Status" }] },
    { label: "Season", category: "General", fields: [{ name: "season", label: "Season" }] },
    { label: "Working_Status", category: "General", fields: [{ name: "working_status", label: "Working Status" }] },
    { label: "GIS Preparation for 1st Proof", category: "Proof Stages", fields: [{ name: `gis1_receive_date`, label: `Receive Date` }, { name: `gis1_operator_name`, label: `Operator name` }, { name: `gis1_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 1st Proof", category: "Proof Stages", fields: [{ name: `carto1_receive_date`, label: `Receive Date` }, { name: `carto1_operator_name`, label: `Operator name` }, { name: `carto1_send_date`, label: `Send Date` }] },
    { label: "1st_proof correction by party", category: "Proof Stages", fields: [{ name: `corr1_receive_date`, label: `Receive Date` }, { name: `corr1_operator_name`, label: `Operator name` }, { name: `corr1_send_date`, label: `Send Date` }] },
    { label: "GIS Preparation for 2nd Proof", category: "Proof Stages", fields: [{ name: `gis2_receive_date`, label: `Receive Date` }, { name: `gis2_operator_name`, label: `Operator name` }, { name: `gis2_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 2nd Proof", category: "Proof Stages", fields: [{ name: `carto2_receive_date`, label: `Receive Date` }, { name: `carto2_operator_name`, label: `Operator name` }, { name: `carto2_send_date`, label: `Send Date` }] },
    { label: "2nd_proof correction by party", category: "Proof Stages", fields: [{ name: `corr2_receive_date`, label: `Receive Date` }, { name: `corr2_operator_name`, label: `Operator name` }, { name: `corr2_send_date`, label: `Send Date` }] },
    { label: "GIS Preparation for 3rd Proof", category: "Proof Stages", fields: [{ name: `gis3_receive_date`, label: `Receive Date` }, { name: `gis3_operator_name`, label: `Operator name` }, { name: `gis3_send_date`, label: `Send Date` }] },
    { label: "Cartrographic Preparation for 3rd Proof", category: "Proof Stages", fields: [{ name: `carto3_receive_date`, label: `Receive Date` }, { name: `carto3_operator_name`, label: `Operator name` }, { name: `carto3_send_date`, label: `Send Date` }] },
    { label: "3rd_proof correction by party", category: "Proof Stages", fields: [{ name: `corr3_receive_date`, label: `Receive Date` }, { name: `corr3_operator_name`, label: `Operator name` }, { name: `corr3_send_date`, label: `Send Date` }] },
    { label: "Boundary Checking", category: "Boundary/Geodesy", fields: [{ name: "boundary_receive_date", label: "Receive Date" }, { name: "boundary_operator_name", label: "Operator name" }, { name: "boundary_send_date", label: "Send Date" }] },
    { label: "Geodesy Checking", category: "Boundary/Geodesy", fields: [{ name: "geodesy_receive_date", label: "Receive Date" }, { name: "geodesy_operator_name", label: "Operator name" }, { name: "geodesy_send_date", label: "Send Date" }] },
    { label: "sheet coreection by D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_receive_date", label: "Receive Date" }, { name: "ddev_corrected_by", label: "Corrected by" }, { name: "ddev_send_date", label: "Send Date" }, { name: "ddev_send_to", label: "Send to" }] },
    { label: "GIs preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_gis_receive_date", label: "Receive Date" }, { name: "ddev_gis_operator_name", label: "Operator name" }, { name: "ddev_gis_send_date", label: "Send Date" }] },
    { label: "cartrophic preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_carto_receive_date", label: "Receive Date" }, { name: "ddev_carto_operator_name", label: "Operator name" }, { name: "ddev_carto_send_date", label: "Send Dates" }] },
    { label: "Director preparation from D.Dev", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "ddev_director_receive_date", label: "Receive Date" }, { name: "ddev_director_corrected_by", label: "corrected By" }, { name: "ddev_director_send_date", label: "Send Date to DPCO" }] },
    { label: "sheet coreection by DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_receive_date", label: "Receive Date" }, { name: "dpco_corrected_by", label: "Corrected by" }, { name: "dpco_send_date", label: "Send Date" }, { name: "dpco_send_to", label: "Send to" }] },
    { label: "GIs preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_gis_receive_date", label: "Receive Date" }, { name: "dpco_gis_operator_name", label: "Operator name" }, { name: "dpco_gis_send_date", label: "Send Date" }] },
    { label: "cartrophic preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_carto_receive_date", label: "Receive Date" }, { name: "dpco_carto_operator_name", label: "Operator name" }, { name: "dpco_carto_send_date", label: "Send Dates" }] },
    { label: "Preparation from DPCO", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_prep_receive_date", label: "Receive Date" }, { name: "dpco_prep_operator_name", label: "Operator name" }, { name: "dpco_prep_send_date", label: "Send Date to Cartrographic" }] },
    { label: "DPCO to GIS", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_to_gis_receive_date", label: "Receive Date" }, { name: "dpco_to_gis_operator_name", label: "Operator name" }, { name: "dpco_to_gis_send_date", label: "Send Dates" }] },
    { label: "DPCO to Carto", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_to_carto_receive_date", label: "Receive Date" }, { name: "dpco_to_carto_operator_name", label: "Operator name" }, { name: "dpco_to_carto_send_date", label: "Send Dates" }] },
    { label: "DPCO to CPT", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "dpco_to_cpt_receive_date", label: "Receive Date" }, { name: "dpco_to_cpt_corrected_by", label: "corrected By" }, { name: "dpco_to_cpt_send_date", label: "Send Date to LPO" }] }, // Note: `dpco_to_CTP_` in Dashboard2Party, `dpco_to_cpt_` in Geodesy. Using Geodesy's version.
    { label: "Print by LPO(Date)", category: "DDEV/DPCO/CPT/LPO", fields: [{ name: "lpo_print_date", label: "Print by LPO (Date)" }] }
  ], []);

  const allFields = useMemo(() => columnStructure.flatMap(group => group.fields), [columnStructure]);

  // ***** IMPORTANT: Update this URL to your actual API endpoint for General Dashboard data *****
  //const API_BASE_URL = "http://localhost:5000/api/general"; // Example: /api/general or /api/dashboard_general_data
  const API_BASE_URL = "https://sheettrackernode.sobalex.com/api/general";
  const getCategoryClass = (category) => {
    // This function is used for styling, taken from DashboardGeodesy.js
    switch (category) {
      case "General": return "general-column-group";
      case "Proof Stages": return "proof-stages-column-group";
      case "Boundary/Geodesy": return "geo-column-group"; // Name from Geodesy.js
      case "DDEV/DPCO/CPT/LPO": return "ddev-dpco-column-group"; // Name from Geodesy.js
      default: return "";
    }
  };

  const fetchData = useCallback(async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || !currentUser.token) {
      navigate('/login');
      return;
    }

    // ***** IMPORTANT: Update 'GeneralUser' to the correct role(s) for this dashboard *****
    if (currentUser.user.role !== 'GeneralUser' && currentUser.user.role !== 'admin') {
      setError('Access Denied: You do not have permission to view this page.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/records`, { headers: { Authorization: `Bearer ${currentUser.token}` } });
      if (res.data.status === "success") {
        setTableData(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setError(res.data.message || "Failed to fetch data.");
        setTableData([]);
      }
    } catch (err) {
      console.error("Fetch error (General Dashboard):", err);
      if (err.response) {
        const errMsg = err.response.data.message || 'Error fetching data.';
        setError(`Server Error ${err.response.status}: ${errMsg}`);
        if (err.response.status === 401 || err.response.status === 403) {
          authService.logout();
          navigate('/login');
        }
      } else if (err.request) {
        setError('No server response. Please check backend server.');
      } else {
        setError('Error setting up request.');
      }
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, API_BASE_URL]); // API_BASE_URL added to dependency array

  useEffect(() => {
    const currentUserOnMount = authService.getCurrentUser();
    if (!currentUserOnMount || !currentUserOnMount.token) {
      navigate('/login');
      return;
    }
    // ***** IMPORTANT: Update 'GeneralUser' to the correct role(s) for this dashboard *****
    if (currentUserOnMount.user.role !== 'GeneralUser' && currentUserOnMount.user.role !== 'admin') {
      setError('Access Denied.');
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const filteredTableData = useMemo(() => {
    let data = tableData;
    if (selectedPartyUnitFilter) { data = data.filter(row => row.party_unit === selectedPartyUnitFilter); }
    if (selectedSeasonFilter) { data = data.filter(row => row.season === selectedSeasonFilter); }
    if (!searchTerm.trim()) { return data; }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return data.filter(row => {
      return allFields.some(field => {
        const value = row[field.name];
        return value !== null && value !== undefined && String(value).toLowerCase().includes(lowercasedSearchTerm);
      });
    });
  }, [tableData, searchTerm, selectedPartyUnitFilter, selectedSeasonFilter, allFields]);

  const statusSummary = useMemo(() => {
    let dataToSummarize = tableData;
    if (selectedPartyUnitFilter) { dataToSummarize = dataToSummarize.filter(row => row.party_unit === selectedPartyUnitFilter); }
    if (selectedSeasonFilter) { dataToSummarize = dataToSummarize.filter(row => row.season === selectedSeasonFilter); }
    
    const counts = { allSheets: dataToSummarize.length };
    statusOptionsForSummaryAndFilter.forEach(status => counts[status] = 0); // Initialize all status counts to 0

    dataToSummarize.forEach(row => {
      if (row.status && statusOptionsForSummaryAndFilter.includes(row.status)) {
        counts[row.status]++;
      }
    });
    return counts;
  }, [tableData, selectedPartyUnitFilter, selectedSeasonFilter]); // statusOptionsForSummaryAndFilter is stable

  const getRowClassByWorkingStatus = (workingStatus) => {
    if (!workingStatus) return '';
    const status = String(workingStatus).trim().toLowerCase();
    switch (status) {
      case 'in progress': return 'row-in-progress';
      case 'pending': return 'row-pending';
      case 'submitted': return 'row-submitted';
      case 'completed': return 'row-completed';
      default: return '';
    }
  };

  if (loading) {
    return ( <div className="container mt-5 text-center"> <div className="spinner-border text-primary" role="status"> <span className="visually-hidden">Loading...</span></div><p>Loading Dashboard Data...</p></div>);
  }
  if (error) {
    return (<div className="container mt-5"><div className="alert alert-danger text-center"><h4>Error Accessing Page</h4><p>{error}</p><Link to="/home" className="btn btn-info mt-2">Go to Home</Link></div></div>);
  }

  return (
    <div className="container-fluid mt-4 dashboard-general-container"> {/* Ensure CSS class matches your CSS file */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h2 className="dashboard-general-header mb-0 me-3">General Dashboard - Sheet Overview</h2>
        {/* "Add Data" Button is REMOVED */}
      </div>

      <Row className="mb-3 align-items-end">
        <Col md={4} lg={3} className="mb-2 mb-md-0">
          <Form.Group controlId="partyUnitFilter">
            <Form.Label className="mb-1"><small>Filter by Party/Unit:</small></Form.Label>
            <Form.Select size="sm" value={selectedPartyUnitFilter} onChange={(e) => setSelectedPartyUnitFilter(e.target.value)} >
              <option value="">All Party/Units</option>
              {partyUnitOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} lg={3} className="mb-2 mb-md-0">
          <Form.Group controlId="seasonFilter">
            <Form.Label className="mb-1"><small>Filter by Season:</small></Form.Label>
            <Form.Select size="sm" value={selectedSeasonFilter} onChange={(e) => setSelectedSeasonFilter(e.target.value)} >
              <option value="">All Seasons</option>
              {seasonOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} lg={4}>
          <Form.Group controlId="searchTerm">
            <Form.Label className="mb-1"><small>Search Table:</small></Form.Label>
            <InputGroup size="sm">
              <Form.Control type="text" placeholder="Search all columns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <InputGroup.Text><SearchIcon /></InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <div className="mb-3 p-3 border rounded bg-light shadow-sm">
        <h6>Status Summary for: <strong className="text-primary">{selectedPartyUnitFilter || 'All Party/Units'}{selectedSeasonFilter && ` (Season: ${selectedSeasonFilter})`}</strong></h6>
        <Row xs={1} sm={2} md={3} lg={4} className="g-2">
            <Col><strong>Total Sheets: {statusSummary.allSheets}</strong></Col>
            {statusOptionsForSummaryAndFilter.map(statusKey => (
                statusSummary[statusKey] > 0 && (
                    <Col key={statusKey}>
                        {statusKey.replace(/_/g, ' ')}: <span className="fw-bold">{statusSummary[statusKey]}</span>
                    </Col>
                )
            ))}
        </Row>
        {statusOptionsForSummaryAndFilter.every(statusKey => (statusSummary[statusKey] || 0) === 0) && statusSummary.allSheets > 0 && (
            <Row><Col><small className="text-muted mt-1 d-block">(No items in specific proof/office statuses for this selection)</small></Col></Row>
        )}
        {statusSummary.allSheets === 0 && (<Row><Col><small className="text-muted mt-1 d-block">(No sheets for this selection)</small></Col></Row>)}
      </div>

      {/* Modals for Add/Edit are REMOVED */}

      <div className="table-responsive">
        <table className="table table-bordered table-sm table-hover">
          <thead>
            <tr>
              {columnStructure.map((group, index) => {
                const isLastGroup = index === columnStructure.length - 1;
                const thClass = `text-center align-middle main-column-header column-group-${index} ${!isLastGroup || group.fields.length > 1 ? 'border-right-group' : ''}`;
                if (group.fields.length === 1) {
                  return ( <th key={`main-${index}`} rowSpan={2} className={thClass} > {group.label} </th> );
                } else {
                  return ( <th key={`main-${index}`} colSpan={group.fields.length} className={thClass} > {group.label} </th> );
                }
              })}
              {/* Actions column header is REMOVED */}
            </tr>
            <tr>
              {columnStructure.flatMap((group, groupIndex) => {
                if (group.fields.length > 1) {
                  return group.fields.map((field, fieldIndex) => {
                    const isLastSubFieldInGroup = fieldIndex === group.fields.length - 1;
                    const headerClasses = `${getCategoryClass(group.category)} sub-column-header ${isLastSubFieldInGroup ? 'border-right-group-header' : ''}`.trim();
                    return ( <th key={`sub-${groupIndex}-${fieldIndex}`} className={headerClasses}> {field.label} </th> );
                  });
                }
                return null;
              })}
            </tr>
          </thead>
          <tbody>
            {filteredTableData.length > 0 ? (
              filteredTableData.map((row, rowIndex) => {
                const rowClass = getRowClassByWorkingStatus(row.working_status);
                return (
                <tr key={row.id || rowIndex} className={rowClass}>
                  {allFields.map((field) => {
                      const groupForField = columnStructure.find(g => g.fields.some(f => f.name === field.name));
                      const isLastFieldInMainGroup = groupForField && groupForField.fields.length > 0 && groupForField.fields[groupForField.fields.length - 1].name === field.name;
                      const isSingleFieldGroup = groupForField && groupForField.fields.length === 1;
                      const categoryClass = groupForField ? getCategoryClass(groupForField.category) : '';
                      const cellClasses = `${categoryClass} ${isLastFieldInMainGroup || isSingleFieldGroup ? 'border-right-group' : ''}`.trim();
                      
                      let cellValue = row[field.name];
                      if (field.name.includes("date") && cellValue) {
                        const date = new Date(cellValue);
                        if (!isNaN(date.getTime())) {
                            // Attempt to parse YYYY-MM-DD from a string like "YYYY-MM-DDTHH:mm:ss.sssZ"
                            const datePart = String(cellValue).split('T')[0];
                            const [year, month, day] = datePart.split('-');
                            if (year && month && day && year.length === 4 && month.length >=1 && month.length <=2 && day.length >=1 && day.length <=2) { 
                                cellValue = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`; 
                            } else { 
                                cellValue = date.toLocaleDateString('en-CA'); // Fallback to YYYY-MM-DD format
                            }
                        } else { 
                            cellValue = ""; // Keep empty if invalid date, was cellValue || "N/A" or cellValue || ""
                        }
                      }
                    return ( <td key={field.name} className={cellClasses}> {cellValue === null || cellValue === undefined ? '' : String(cellValue)} </td> );
                  })}
                  {/* Actions cell with Edit button is REMOVED */}
                </tr>
              );
            })
            ) : (
              <tr><td colSpan={allFields.length} className="text-center py-4"> {/* Adjusted colSpan */}
                {searchTerm || selectedPartyUnitFilter || selectedSeasonFilter ? 'No matching records found.' : 'No data available.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DashboardGeneral;